/**
 * usePersistLanguage — syncs the active i18n language with:
 *   1. localStorage (always, for unauthenticated pages)
 *   2. User.uiLanguage in the DB (when authenticated)
 *
 * Usage: call once at app root (SideBar / ThemeContextProvider level).
 * The hook is a no-op for unauthenticated users — they still get localStorage.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axiosInstance from "../services/axiosInstance";

export function usePersistLanguage() {
  const { i18n } = useTranslation();
  const userId    = useSelector((s) => s.auth?.user?.id ?? s.auth?.userId ?? null);
  const didMount  = useRef(false);

  useEffect(() => {
    // On mount: load saved language from server (authenticated users)
    if (!userId) return;
    axiosInstance.get("/User/me/language")
      .then((r) => {
        const serverLang = r.data?.uiLanguage;
        if (serverLang && serverLang !== i18n.language) {
          i18n.changeLanguage(serverLang);
        }
      })
      .catch(() => {}); // silent — fallback to localStorage is already applied
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return; }
    // On language change after mount: persist to DB (authenticated users)
    if (!userId) return;
    axiosInstance.patch("/User/me/language", { uiLanguage: i18n.language }).catch(() => {});
  }, [i18n.language, userId]);
}
